/* Chart export — screen → deck egress (chart-pack §4 "Copy as SVG/PNG", AM
 * persona-2 P0). Turns a live chart SVG into a STANDALONE artifact an AM can
 * drop into a QBR deck, with its provenance baked in.
 *
 * HONESTY (the whole point): a chart pasted into a deck WITHOUT its provenance
 * is the misuse this system exists to prevent. So every export embeds the chart
 * title + the provenance caption INTO the artifact itself — the exported SVG/PNG
 * carries "…across N polled days · IMPACT · window" as visible text, and the
 * copy-as-table path carries the underlying numbers for a deck appendix. There
 * is no way to export the mark without its context.
 *
 * SELF-CONTAINED: the live SVG styles come from app.css classes + design tokens.
 * A file opened in Slides/Keynote has none of that, so we INLINE the computed
 * presentation styles (fill/stroke/font — tokens already resolved to concrete
 * values) onto a clone before serializing. PNG is rendered client-side via
 * canvas (SVG → data-URL → <img> → canvas → blob) — no new dependency.
 */
import { html, svg, type TemplateResult } from 'lit';

const SVG_NS = 'http://www.w3.org/2000/svg';

// Presentation properties that carry a chart's look — copied from computed
// style so the artifact renders identically without app.css.
const PRESENTATION_PROPS = [
  'fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-linecap',
  'stroke-linejoin', 'stroke-dasharray', 'opacity', 'font-family', 'font-size',
  'font-weight', 'font-style', 'letter-spacing', 'text-anchor',
  'dominant-baseline', 'font-variant-numeric',
];

/** Resolve a design token to its concrete value against the live document (so
 * the export doesn't depend on CSS variables the deck won't have). */
function token(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Copy computed presentation styles from every node of `src` onto the matching
 * node of `clone` (same tree shape) as inline styles — flattening the CSS
 * cascade + token resolution into a standalone SVG. */
function inlineComputedStyles(src: Element, clone: Element): void {
  const cs = getComputedStyle(src);
  const decls: string[] = [];
  for (const prop of PRESENTATION_PROPS) {
    const val = cs.getPropertyValue(prop);
    if (val && val !== 'normal' && val !== 'none' && !(prop === 'fill' && val === 'rgb(0, 0, 0)' && src.tagName === 'svg')) {
      decls.push(`${prop}:${val}`);
    }
  }
  if (decls.length) {
    const existing = clone.getAttribute('style');
    clone.setAttribute('style', (existing ? existing + ';' : '') + decls.join(';'));
  }
  const srcKids = src.children;
  const cloneKids = clone.children;
  for (let i = 0; i < srcKids.length; i++) {
    const sk = srcKids[i], ck = cloneKids[i];
    if (sk && ck) inlineComputedStyles(sk, ck);
  }
}

/** Naive width-budgeted wrap of a caption at its ` · ` separators, so a long
 * provenance line doesn't overflow the exported width. Returns 1–2 lines. */
export function wrapCaption(caption: string, maxChars: number): string[] {
  const s = (caption || '').trim();
  if (s.length <= maxChars) return s ? [s] : [];
  const parts = s.split(' · ');
  const lines: string[] = [];
  let cur = '';
  for (const p of parts) {
    const next = cur ? `${cur} · ${p}` : p;
    if (next.length > maxChars && cur) {
      lines.push(cur);
      cur = p;
    } else {
      cur = next;
    }
    if (lines.length === 1) break; // cap at 2 lines; put the rest on line 2
  }
  const consumed = lines.join(' · ').length;
  const rest = s.slice(consumed).replace(/^\s*·\s*/, '').trim();
  if (rest) lines.push(rest);
  return lines.slice(0, 2);
}

export interface ExportMeta {
  title: string;
  caption: string;
  /* HONESTY (coverage-honesty audit P2-3): a gap/hole footnote (e.g.
   * "no capture 01-07 → 02-03") lives on-screen as a sibling <div> OUTSIDE the
   * chart <svg>, so a bare SVG/PNG export used to drop the precise gap dates —
   * exactly the misuse this export path exists to prevent. When present, it is
   * baked into the exported artifact as its own muted line beneath the
   * provenance caption, same spirit as the already-baked caption. */
  footnote?: string;
}

/* Shared caption/footnote line-wrap + height math, used by BOTH the SVG
 * serializer and the PNG sizer so the two can never drift on how tall the
 * grown artifact is (a drift would clip the baked caption/footnote). */
function captionBlock(w: number, meta: ExportMeta): {
  capLines: string[];
  footLines: string[];
  titleH: number;
  capH: number;
} {
  const maxChars = Math.floor(w / 5.4);
  const capLines = wrapCaption(meta.caption, maxChars);
  const footLines = meta.footnote ? wrapCaption(meta.footnote, maxChars) : [];
  const totalLines = capLines.length + footLines.length;
  return { capLines, footLines, titleH: meta.title ? 30 : 8, capH: totalLines ? totalLines * 15 + 10 : 6 };
}

/** Serialize a live chart `<svg>` into a standalone SVG string with inlined
 * styles, a white backdrop, and the title + caption baked in as visible text
 * (provenance travels with the mark). Pure DOM → string; no network. */
export function serializeChartSvg(source: SVGSVGElement, meta: ExportMeta): string {
  const clone = source.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(source, clone);

  const vb = (source.getAttribute('viewBox') || `0 0 ${source.clientWidth || 640} ${source.clientHeight || 260}`)
    .split(/\s+/)
    .map(Number);
  const [x = 0, y = 0, w = 640, h = 260] = vb.length === 4 ? vb : [0, 0, 640, 260];

  const { capLines, footLines, titleH, capH } = captionBlock(w, meta);
  const pad = 8;
  const nx = x - pad;
  const ny = y - titleH;
  const nw = w + pad * 2;
  const nh = h + titleH + capH;

  // CSS-keyword fallbacks (no raw hex — adherence): only used if a token fails
  // to resolve, which it won't (tokens.css is loaded); the real values come from
  // the live computed style.
  const ink = token('--ink', 'black');
  const muted = token('--muted', 'gray');
  const white = token('--white', 'white');
  const sans = token('--sans', 'Inter, sans-serif');

  clone.setAttribute('viewBox', `${nx} ${ny} ${nw} ${nh}`);
  clone.setAttribute('width', String(nw));
  clone.setAttribute('height', String(nh));
  clone.setAttribute('xmlns', SVG_NS);

  const bg = document.createElementNS(SVG_NS, 'rect');
  bg.setAttribute('x', String(nx));
  bg.setAttribute('y', String(ny));
  bg.setAttribute('width', String(nw));
  bg.setAttribute('height', String(nh));
  bg.setAttribute('fill', white);
  clone.insertBefore(bg, clone.firstChild);

  // font via SVG presentation ATTRIBUTES (not a style string) so the exported
  // text is self-contained without inventing a font-family literal.
  const textAttrs = (el: Element, size: string, weight: string, fill: string) => {
    el.setAttribute('font-family', sans);
    el.setAttribute('font-size', size);
    if (weight) el.setAttribute('font-weight', weight);
    el.setAttribute('fill', fill);
  };
  if (meta.title) {
    const t = document.createElementNS(SVG_NS, 'text');
    t.setAttribute('x', String(x));
    t.setAttribute('y', String(y - titleH + 18));
    textAttrs(t, '14', '600', ink);
    t.textContent = meta.title;
    clone.appendChild(t);
  }
  // Provenance caption, then the gap/hole footnote continues on the lines
  // directly below it (P2-3) — same muted treatment, so the precise gap dates
  // travel with the exported mark instead of being dropped with the on-screen
  // sibling <div>.
  [...capLines, ...footLines].forEach((line, i) => {
    const c = document.createElementNS(SVG_NS, 'text');
    c.setAttribute('x', String(x));
    c.setAttribute('y', String(y + h + 16 + i * 15));
    textAttrs(c, '11', '', muted);
    c.textContent = line;
    clone.appendChild(c);
  });

  return `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(clone)}`;
}

/** Render an SVG string to a PNG blob at `scale`× via canvas — client-side, no
 * dependency. Rejects if the browser can't rasterize (caller falls back). */
export function svgStringToPng(svgString: string, width: number, height: number, scale = 2): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('no 2d context'));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
    };
    img.onerror = () => reject(new Error('svg image load failed'));
    img.src = url;
  });
}

/** Current-standing TSV for a copy-as-table (deck appendix). Pure. */
export function tableToTsv(cols: string[], rows: Array<Array<string | number>>): string {
  const esc = (v: string | number) => String(v).replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
  return [cols.map(esc).join('\t'), ...rows.map((r) => r.map(esc).join('\t'))].join('\n');
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** slug for a filename from the chart title. */
function slug(s: string): string {
  return (s || 'chart').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'chart';
}

/** The nearest exportable chart SVG to a clicked export button. */
function findChartSvg(fromButton: EventTarget | null): SVGSVGElement | null {
  const el = fromButton as HTMLElement | null;
  const wrap = el?.closest('.chartwrap');
  return (wrap?.querySelector('svg.chart') as SVGSVGElement | null) || null;
}

/* ── the export toolbar (one per chart) ── */

const EX_ICON = {
  svg: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M12 3v12M8 11l4 4 4-4"/><path d="M5 21h14"/></svg>`,
  png: svg`<svg class="mi" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 16l5-5 4 4 3-3 6 6"/><circle cx="8.5" cy="9.5" r="1.5"/></svg>`,
  table: svg`<svg class="mi" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M3 15h18M9 4v16"/></svg>`,
};

async function toast(msg: string): Promise<void> {
  // lightweight, dependency-free confirmation — reuse the workspace toast if the
  // AM board is live, else a transient title-less DOM note.
  const g = globalThis as { WS?: { toast?: (msg: string) => void } };
  if (g.WS && typeof g.WS.toast === 'function') { g.WS.toast(msg); return; }
  const n = document.createElement('div');
  n.className = 'chart-export-toast';
  n.textContent = msg;
  document.body.appendChild(n);
  setTimeout(() => n.remove(), 2200);
}

/** A per-chart export toolbar. Placed inside a `.chartwrap` alongside the chart
 * SVG; its handlers locate that SVG at click time and export it WITH the title
 * + caption baked in. `tableCols`/`tableRows` drive copy-as-table. */
export function chartExportBar(opts: {
  title: string;
  caption: string;
  /* gap/hole footnote text baked into the export alongside the caption (P2-3). */
  footnote?: string;
  tableCols: string[];
  tableRows: Array<Array<string | number>>;
}): TemplateResult {
  const meta: ExportMeta = { title: opts.title, caption: opts.caption, footnote: opts.footnote };

  const doSvg = async (e: MouseEvent) => {
    const s = findChartSvg(e.currentTarget);
    if (!s) return;
    const str = serializeChartSvg(s, meta);
    download(new Blob([str], { type: 'image/svg+xml' }), `${slug(opts.title)}.svg`);
    await toast('SVG downloaded — with its provenance caption');
  };
  const doPng = async (e: MouseEvent) => {
    const s = findChartSvg(e.currentTarget);
    if (!s) return;
    const str = serializeChartSvg(s, meta);
    const vb = (s.getAttribute('viewBox') || '0 0 640 260').split(/\s+/).map(Number);
    const [, , w = 640, h = 260] = vb.length === 4 ? vb : [0, 0, 640, 260];
    // the serialized SVG grew for title+caption+footnote; size the raster to
    // match it via the SAME height math the serializer uses (no drift).
    const { titleH, capH } = captionBlock(w, meta);
    const grownH = h + titleH + capH;
    try {
      const blob = await svgStringToPng(str, w + 16, grownH, 2);
      try {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        await toast('PNG copied — paste into your deck');
      } catch {
        download(blob, `${slug(opts.title)}.png`); // clipboard-image unsupported → download
        await toast('PNG downloaded — with its provenance caption');
      }
    } catch {
      await toast('PNG export unavailable in this browser — use SVG');
    }
  };
  const doTable = async () => {
    const ok = await copyText(tableToTsv(opts.tableCols, opts.tableRows));
    await toast(ok ? 'Table copied — paste into a deck appendix' : 'Copy failed — open “View as table”');
  };

  return html`<div class="chart-export" role="group" aria-label="Export this chart">
    <button type="button" class="pill-act pill-sm" @click=${doSvg} title="Download as SVG (vector, keeps the provenance caption)">${EX_ICON.svg}<span>SVG</span></button>
    <button type="button" class="pill-act pill-sm" @click=${doPng} title="Copy as PNG to paste into a deck (keeps the provenance caption)">${EX_ICON.png}<span>PNG</span></button>
    <button type="button" class="pill-act pill-sm" @click=${doTable} title="Copy the data as a table for a deck appendix">${EX_ICON.table}<span>Table</span></button>
  </div>`;
}
